import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from '@store/authStore'
import { authService } from '@services/authService'
import { useEffect, useState, lazy, Suspense } from 'react'
import ScrollToTop from '@components/common/ScrollToTop'
import CosmicLoader from '@components/common/CosmicLoader'
import { DEFAULT_RESPONSE_LEVEL } from '@config/pricing'

// Public pages (eagerly loaded for SEO and fast first paint)
import Landing from '@pages/public/landing/Landing'
import Login from '@pages/public/login/Login'
import Contact from '@pages/public/contact/Contact'
import Terms from '@pages/public/terms/Terms'
import Privacy from '@pages/public/privacy/Privacy'
import Refunds from '@pages/public/refunds/Refunds'
import Panchang from '@pages/public/panchang/Panchang'
import Horoscope from '@pages/public/horoscope/Horoscope'
import FreeKundli from '@pages/public/free-kundli/FreeKundli'
import FreeCompatibility from '@pages/public/free-compatibility/FreeCompatibility'
import Discussion from '@pages/public/discussion/Discussion'
import TransitSearch from '@pages/public/transit-search/TransitSearch'
import NotFound from '@pages/public/not-found/NotFound'

// Dashboard pages (lazy loaded - only needed after auth)
const Dashboard = lazy(() => import('@pages/dashboard/dashboard/Dashboard'))
const Chat = lazy(() => import('@pages/dashboard/chat/Chat'))
const Profile = lazy(() => import('@pages/dashboard/profile/Profile'))
const Notifications = lazy(() => import('@pages/dashboard/notifications/Notifications'))
// Admin pages (lazy loaded - rarely accessed)
const AdminDashboard = lazy(() => import('@pages/admin/AdminDashboard'))

// Protected Route Component
interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore()
  const [redirecting, setRedirecting] = useState(false)

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      setRedirecting(true)
    }
  }, [isLoading, isAuthenticated])

  if (isLoading || redirecting) {
    if (redirecting) {
      return (
        <>
          <CosmicLoader message="Signing Out" submessage="See you among the stars..." />
          <Navigate to="/login" replace />
        </>
      )
    }
    return <CosmicLoader />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Admin Route Component
interface AdminRouteProps {
  children: React.ReactNode
}

const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore()

  return (
    <ProtectedRoute>
      {isAuthenticated && user?.is_admin ? (
        <>{children}</>
      ) : (
        <Navigate to="/dashboard" replace />
      )}
    </ProtectedRoute>
  )
}

// ... existing imports ...

// ... existing ProtectedRoute and AdminRoute components ...

function App() {
  const { setUser, setLoading, logout, isLoading } = useAuthStore()

  useEffect(() => {
    // Check authentication on mount (Global)
    const checkAuth = async () => {
      try {
        const response = await authService.checkAuth()
        if (response.authenticated && response.user) {
          const apiUser = response.user as any
          const userData = {
            ...response.user,
            _id: apiUser.id || apiUser._id,
            wallet_balance: apiUser.wallet_balance || 0,
            is_admin: apiUser.is_admin || false,
            response_level: apiUser.response_level || DEFAULT_RESPONSE_LEVEL,
            created_at: apiUser.created_at || new Date().toISOString()
          }
          setUser(userData)
        } else {
          logout()
        }
      } catch (error) {
        logout()
      } finally {
        setLoading(false)
      }
    }

    if (isLoading) {
      checkAuth()
    }
  }, [isLoading, setUser, setLoading])

  // Show universal loader during initial auth check
  if (isLoading) {
    return <CosmicLoader />
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/panchang/:lang?/:date?/:locationName?/:lat?/:lon?" element={<Panchang />} />
        <Route path="/horoscope/:lang?/:period?/:sign?/:date?" element={<Horoscope />} />
        <Route path="/free-kundli/:lang?" element={<FreeKundli />} />
        <Route path="/kundli-matching/:lang?" element={<FreeCompatibility />} />
        <Route path="/discussion" element={<Discussion />} />
        <Route path="/discussion/:discussionId" element={<Discussion />} />
        <Route path="/transit-search/:lang?" element={<TransitSearch />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refunds" element={<Refunds />} />

        {/* Protected routes (lazy loaded) */}
        <Route path="/dashboard" element={<Navigate to="/dashboard/services" replace />} />
        <Route
          path="/dashboard/notifications"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CosmicLoader />}>
                <Notifications />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard/:tab"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CosmicLoader />}>
                <Dashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Chat is only meaningful with a session; bare /chat rendered a dead UI. */}
        <Route path="/chat" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/chat/:sessionId"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CosmicLoader />}>
                <Chat />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="/profile" element={<Navigate to="/profile/overview" replace />} />
        <Route
          path="/profile/:tab"
          element={
            <ProtectedRoute>
              <Suspense fallback={<CosmicLoader />}>
                <Profile />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Admin routes (lazy loaded) */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route
          path="/admin/:tab"
          element={
            <AdminRoute>
              <Suspense fallback={<CosmicLoader />}>
                <AdminDashboard />
              </Suspense>
            </AdminRoute>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  )
}

export default App
